<script setup lang="ts">
/**
 * UI-kit component
 * Category: 'Inputs'
 * Name: Input
 * FIGMA: 'https://www.figma.com/design/oRsJUOAucnhxx5aeqy4TRO/APP-admin?node-id=1671-21058&t=ldymHtYK0zJDKBqF-4
 */
import type { InputPrimaryProps, InputPrimaryEvent } from '../types/interfaces'

import { AppText } from '@shared/ui/typography'

import { useInputPrimary } from '../composables/use-input-primary.hook'

defineOptions({
  name: 'InputPrimary',
})

const props = withDefaults(defineProps<InputPrimaryProps>(), {
  id: null,
  name: '',
  type: 'text',
  value: '',
  label: '',
  ariaLabel: '',
  className: '',
  inputClass: '',
  placeholder: '',
  optionalLabel: '',
  disabled: false,
  min: null,
  max: null,
  mode: 'normal',
  required: false,
  readonly: false,
  step: null,
  maxLength: undefined,
})

const emit = defineEmits<{
  'update:value': [InputPrimaryEvent['value']]
  change: [InputPrimaryEvent]
  input: [InputPrimaryEvent]
  focus: [InputPrimaryEvent]
  blur: [InputPrimaryEvent]
  'keyup:enter': [InputPrimaryEvent]
}>()

const {
  value,
  refIsError,
  refIsSuccess,
  handleChange,
  handleInput,
  handleFocus,
  handleBlur,
  handleKeyupEnter,
} = useInputPrimary({
  props,
  onUpdateValue: (value: InputPrimaryEvent['value']) => emit('update:value', value),
  onChange: (event: InputPrimaryEvent) => emit('change', event),
  onInput: (event: InputPrimaryEvent) => emit('input', event),
  onFocus: (event: InputPrimaryEvent) => emit('focus', event),
  onBlur: (event: InputPrimaryEvent) => emit('blur', event),
  onKeyupEnter: (event: InputPrimaryEvent) => emit('keyup:enter', event),
})
</script>

<template>
  <div :class="['ui-input-primary', className]">
    <div class="ui-input-primary__label">
      <AppText
        v-if="label"
        name="body2"
        tag="label"
        :for="id"
      >
        {{ label }} <span
          v-if="required"
          class="ui-input-primary__required"
        >*</span>
      </AppText>
      <AppText
        v-if="optionalLabel"
        name="body2"
        class="optional-label"
      >
        {{ optionalLabel }}
      </AppText>
    </div>
    <textarea
      v-if="type === 'textArea'"
      :id="id ?? 'textArea'"
      v-model="value"
      :aria-label="ariaLabel || undefined"
      :class="[inputClass, { 'is-error': refIsError, 'is-success': refIsSuccess, readonly }]"
      :placeholder="placeholder"
      :readonly="readonly"
      :disabled="disabled"
      :maxlength="maxLength"
      @change="handleChange"
      @input="handleInput"
      @focus="handleFocus"
      @blur="handleBlur"
      @wheel="$event.stopPropagation()"
    />
    <div
      v-else
      class="ui-input-primary__input"
    >
      <input
        :id="id ?? 'input'"
        v-model="value"
        :aria-label="ariaLabel || undefined"
        :name="name"
        :class="[inputClass, { 'is-error': refIsError, 'is-success': refIsSuccess, readonly }]"
        :type="type"
        :placeholder="placeholder"
        :disabled="disabled"
        :readonly="readonly"
        :min="min ?? undefined"
        :max="max ?? undefined"
        :maxlength="maxLength"
        :step="step ?? undefined"
        @change="handleChange"
        @input="handleInput"
        @focus="handleFocus"
        @blur="handleBlur"
        @keyup.enter="handleKeyupEnter"
      >
      <div v-if="$slots.icon">
        <slot name="icon" />
      </div>
      <div v-if="$slots.iconError && refIsError">
        <slot name="iconError" />
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped src="../styles/_input-primary.scss" />
